import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { fetchCurrencies } from 'actions';
import { getCurrenciesFetching, getCurrenciesItems } from 'selectors';
import {
  DEFAULT_SOURCE_CURRENCY,
  getCurrencyYearArchivePath,
  getHrefCurrency,
} from 'utils';

import CurrencyConverterDropdown from 'components/CurrencyConverterDropdown';

import { MoreSelectWrapper, Tab, Wrapper } from './styles';

const CurrencyYearArchiveCurrencyTabs = ({ year, availableCurrencies }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { locale } = useIntl();
  const { currency = DEFAULT_SOURCE_CURRENCY } = useParams();
  const activeId = currency.toUpperCase();
  const currenciesItems = useSelector(getCurrenciesItems);
  const currenciesIsFetching = useSelector(getCurrenciesFetching);

  const primaryOptions = useMemo(
    () =>
      (availableCurrencies || []).map(code => ({
        id: String(code).toUpperCase(),
      })),
    [availableCurrencies]
  );

  const displayOptions = useMemo(() => {
    if (!activeId) {
      return primaryOptions;
    }

    if (primaryOptions.find(option => option.id === activeId)) {
      return primaryOptions;
    }

    if (!primaryOptions.length) {
      return [{ id: activeId }];
    }

    return [
      ...primaryOptions.slice(0, primaryOptions.length - 1),
      { id: activeId },
    ];
  }, [activeId, primaryOptions]);

  useEffect(() => {
    if (!currenciesItems?.money?.length) {
      dispatch(fetchCurrencies({ type: 'money', locale }));
    }
  }, [currenciesItems?.money?.length, dispatch, locale]);

  const fetchCurrenciesByScroll = useCallback(
    type => {
      dispatch(fetchCurrencies({ type, locale }));
    },
    [dispatch, locale]
  );

  const onCurrencyChange = useCallback(
    value => {
      const [code] = String(value).split('-');
      const path = getCurrencyYearArchivePath(code, year);

      if (path) {
        history.push(path);
      }
    },
    [history, year]
  );

  const onTabClick = useCallback(
    (event, path) => {
      event.preventDefault();

      if (path) {
        history.push(path);
      }
    },
    [history]
  );

  const hasDropdown =
    Boolean(currenciesItems?.money?.length) ||
    Boolean(currenciesItems?.all?.length) ||
    currenciesIsFetching;

  return (
    <Wrapper>
      {displayOptions.map(({ id }) => {
        const tabCurrency = id.toLowerCase();
        const path = getCurrencyYearArchivePath(tabCurrency, year);

        return (
          <Tab
            key={id}
            href={path ? getHrefCurrency(path, locale) : undefined}
            $active={activeId === id}
            aria-current={activeId === id ? 'page' : undefined}
            onClick={event => onTabClick(event, path)}
          >
            {id}
          </Tab>
        );
      })}
      {hasDropdown ? (
        <MoreSelectWrapper>
          <CurrencyConverterDropdown
            currencyInfo={{ code: activeId, type: 'money' }}
            items={currenciesItems}
            loading={currenciesIsFetching}
            onSelectOption={onCurrencyChange}
            onScrollEnd={fetchCurrenciesByScroll}
            listWrapperStyle={{ left: 0, right: 'auto', marginTop: 8 }}
            isButtonMode
          />
        </MoreSelectWrapper>
      ) : null}
    </Wrapper>
  );
};

CurrencyYearArchiveCurrencyTabs.propTypes = {
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  availableCurrencies: PropTypes.arrayOf(PropTypes.string),
};

CurrencyYearArchiveCurrencyTabs.defaultProps = {
  availableCurrencies: [],
};

export default CurrencyYearArchiveCurrencyTabs;
